/**
 * Phase 24.7.4 T3 — frontend client for shopping list endpoints.
 *
 * Mirrors backend ShoppingListResponse / ShoppingListToggleResponse
 * Pydantic schemas (app/schemas/shopping_list.py). Backend renders
 * `category_label` server-side via Accept-Language; frontend uses it
 * directly (no client-side category i18n).
 *
 * Toggle target is `row.id` (ShoppingListItem PK) — same ingredient в
 * different units yields multiple toggle-rows under one ingredient name.
 */
import { api } from "./client";

export interface ShoppingListRow {
  id: number;
  amount: number;
  unit: string;
  is_checked: boolean;
  in_pantry: boolean;
}

export interface ShoppingListItemEntry {
  ingredient_id: number;
  ingredient_name: string; // localized via Accept-Language
  ingredient_name_en: string; // canonical English
  rows: ShoppingListRow[]; // multi-row when same ingredient in mixed units
}

export interface ShoppingListCategoryGroup {
  category: string;
  category_label: string; // server-rendered, Accept-Language localized
  items: ShoppingListItemEntry[];
}

export interface ShoppingListWarning {
  key: string;
  params?: Record<string, unknown>;
}

export interface ShoppingListResponse {
  plan_id: number;
  by_category: ShoppingListCategoryGroup[];
  warnings: ShoppingListWarning[];
}

export interface ShoppingListToggleResponse {
  id: number;
  is_checked: boolean;
  in_pantry: boolean;
}

export type ToggleField = "is_checked" | "in_pantry";

export async function getShoppingList(
  planId: number,
): Promise<ShoppingListResponse> {
  const { data } = await api.get<ShoppingListResponse>(
    `/meal-plan/${planId}/shopping-list`,
  );
  return data;
}

export async function toggleShoppingListItem(
  itemId: number,
  field: ToggleField,
): Promise<ShoppingListToggleResponse> {
  const { data } = await api.post<ShoppingListToggleResponse>(
    `/shopping-list-items/${itemId}/toggle?field=${field}`,
  );
  return data;
}
